import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, LogOut, FileText, PlayCircle, Activity, LayoutDashboard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { AnimatedStickyHeader } from '@/components/ui/animated-sticky-header';
import { toast } from 'sonner';
import { GoogleAIFundBadge } from '@/components/ui/GoogleAIFundBadge';

// Dynamic imports for hero images — only loaded on desktop when visible
const FloatingImage = React.lazy(() => import('./FloatingImage').then(m => ({ default: m.FloatingImage })));

export function CareerHeroSection() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [showFloatingImages, setShowFloatingImages] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lazy-load floating images only on desktop after initial render
  React.useEffect(() => {
    if (window.innerWidth >= 1024) {
      const timer = setTimeout(() => setShowFloatingImages(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut({ scope: 'local' });
      try {
        localStorage.removeItem('sb-jknbrmgwzizdtvtnufwb-auth-token');
      } catch (e) {
        console.error('Failed to clear localStorage:', e);
      }
      toast.success("Logged out successfully");
      window.location.href = "/";
    } catch (error) {
      console.error("Logout error:", error);
      try {
        localStorage.removeItem('sb-jknbrmgwzizdtvtnufwb-auth-token');
      } catch (e) {
        console.error('Failed to clear localStorage:', e);
      }
      toast.success("Logged out successfully");
      window.location.href = "/";
    }
  };

  const navItems = [
    { name: "Home", href: "/", isActive: location.pathname === "/" || location.pathname === "/homepage2" },
    { name: "Resume Builder", href: "/resume-builder", isActive: location.pathname.includes("/resume-builder") },
    { name: "Interview", href: "/interview", isActive: location.pathname === "/interview" },
    { name: "Career Discovery", href: "/career-discovery", isActive: location.pathname.includes("/career-discovery") },
  ];

  const desktopAuthButtons = user ? (
    <div className="flex items-center space-x-4">
      <button
        onClick={() => navigate("/interview")}
        className="flex items-center text-gray-600 hover:text-brand-blue font-medium text-sm gap-2 transition-colors"
      >
        <LayoutDashboard size={18} />
        <span>Dashboard</span>
      </button>
      <button
        onClick={handleLogout}
        className="flex items-center text-gray-600 hover:text-brand-blue font-medium text-sm gap-2 transition-colors"
      >
        <LogOut size={18} />
        <span>Logout</span>
      </button>
    </div>
  ) : (
    <Button
      onClick={() => navigate("/auth")}
      className="rounded-full bg-brand-blue text-white hover:bg-blue-800"
      size="sm"
    >
      Get Started
    </Button>
  );

  const mobileAuthButtons = user ? (
    <div className="flex flex-col gap-3 w-full">
      <Button
        onClick={() => {
          navigate("/interview");
        }}
        className="w-full rounded-full"
        variant="outline"
      >
        Dashboard
      </Button>
      <Button
        onClick={handleLogout}
        className="w-full rounded-full"
        variant="outline"
      >
        <LogOut className="h-4 w-4 mr-2" />
        Logout
      </Button>
    </div>
  ) : (
    <div className="flex flex-col gap-3 w-full">
      <Button
        onClick={() => navigate("/auth")}
        className="w-full rounded-full bg-foreground text-background hover:bg-foreground/90"
      >
        Get Started
      </Button>
    </div>
  );

  const logo = (
    <a href="/" className="text-2xl font-extrabold text-brand-blue tracking-tight">
      careerfy
    </a>
  );

  return <>
      <AnimatedStickyHeader
        logo={logo}
        navItems={navItems}
        authButtons={{
          desktop: desktopAuthButtons,
          mobile: mobileAuthButtons,
        }}
      />
      {/* Spacer for fixed header */}
      <div className="h-20" aria-hidden="true" />
      <main className="overflow-hidden">
        <div aria-hidden className="z-[2] absolute inset-0 pointer-events-none isolate opacity-50 contain-strict hidden lg:block">
          <div className="w-[35rem] h-[80rem] -translate-y-[350px] absolute left-0 top-0 -rotate-45 rounded-full bg-[radial-gradient(68.54%_68.72%_at_55.02%_31.46%,hsl(var(--primary)/.08)_0,hsl(var(--primary)/.02)_50%,transparent_80%)]" />
          <div className="h-[80rem] absolute right-0 top-0 w-56 rotate-45 rounded-full bg-[radial-gradient(50%_50%_at_50%_50%,hsl(var(--secondary)/.06)_0,hsl(var(--secondary)/.02)_80%,transparent_100%)] [translate:-5%_-50%]" />
        </div>
        
        <section>
          <div className="relative pt-16 md:pt-28 bg-background">
            <div aria-hidden className="absolute inset-0 -z-10 size-full [background:radial-gradient(125%_125%_at_50%_100%,transparent_0%,hsl(var(--background))_75%)]" />
            
            {/* Floating Images — hidden for now */}
            
            <div className="mx-auto max-w-7xl px-6 relative z-10">
              <div className="text-center sm:mx-auto lg:mr-auto lg:mt-0">
                
                {/* H1 with Orange Underline */}
                <h1 
                  className="text-5xl md:text-7xl font-extrabold text-gray-900 tracking-tight mb-6 leading-[1.1]"
                  style={{ minHeight: '120px', contain: 'layout' }}
                >
                  Your Path to a <br className="hidden md:block" />
                  <span className="text-brand-blue inline-block relative">
                    Dream Job
                    <svg className="absolute w-full h-3 -bottom-1 left-0 text-brand-orange -z-10 opacity-70" viewBox="0 0 100 10" preserveAspectRatio="none">
                      <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="8" fill="none" />
                    </svg>
                  </span>
                </h1>
                
                {/* CSS-first subtitle — visible immediately, no JS dependency */}
                <p 
                  className="mx-auto mt-8 max-w-2xl text-balance text-lg md:text-xl text-muted-foreground leading-relaxed animate-fade-in"
                  style={{ minHeight: '56px', contain: 'layout' }}
                >
                  Land interviews at top companies with AI-powered resume optimization and realistic interview practice
                </p>

                {/* CTA Buttons */}
                <div className="mt-10 flex flex-col sm:flex-row gap-4 w-full justify-center">
                  <Button
                    asChild
                    className="px-6 py-3 rounded-full font-semibold bg-brand-blue hover:bg-blue-800 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <Link to="/resume-builder">
                      <FileText size={20} />
                      Build Resume
                    </Link>
                  </Button>
                  
                  <Button
                    asChild
                    variant="outline"
                    className="px-6 py-3 rounded-full font-semibold border-2 border-gray-200 text-gray-700 hover:border-brand-blue hover:text-brand-blue bg-white transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <Link to="/interview">
                      <PlayCircle size={20} />
                      Practice Interview
                    </Link>
                  </Button>
                  
                  <Button
                    asChild
                    variant="outline"
                    className="px-6 py-3 rounded-full font-semibold border-2 border-gray-200 text-gray-700 hover:border-brand-blue hover:text-brand-blue bg-white transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <Link to="/career-discovery">
                      <Activity size={20} />
                      Career DNA
                    </Link>
                  </Button>
                </div>

                {/* Google AI Fund Badge */}
                <div className="mt-8 flex justify-center">
                  <GoogleAIFundBadge variant="featured" />
                </div>

              </div>
            </div>
          </div>
        </section>
        
      </main>
    </>;
}

// Separate component that dynamically imports the images
function FloatingImagesBlock() {
  const [images, setImages] = React.useState<{ heroResume?: string; heroInterview?: string; heroCareerDna?: string; heroCoverLetter?: string }>({});

  React.useEffect(() => {
    // Dynamic imports — these are code-split out of the main bundle
    Promise.all([
      import('@/assets/hero-resume.jpg'),
      import('@/assets/hero-interview.jpg'),
      import('@/assets/hero-career-dna.jpg'),
      import('@/assets/hero-cover-letter.jpg'),
    ]).then(([resume, interview, careerDna, coverLetter]) => {
      setImages({
        heroResume: resume.default,
        heroInterview: interview.default,
        heroCareerDna: careerDna.default,
        heroCoverLetter: coverLetter.default,
      });
    });
  }, []);

  if (!images.heroResume) return null;

  return (
    <div className="hidden absolute inset-0 max-w-7xl mx-auto pointer-events-none lg:block">
      <React.Suspense fallback={null}>
        <FloatingImage src={images.heroResume} alt="Resume Builder" position="top-10 left-10 lg:left-20 xl:left-0" animationDelay="0s" rotation="-6deg" />
        <FloatingImage src={images.heroInterview!} alt="Interview Practice" position="top-1/2 -translate-y-1/2 left-20 lg:left-32 xl:left-12" animationDelay="2s" rotation="3deg" />
        <FloatingImage src={images.heroCareerDna!} alt="Career DNA Discovery" position="top-20 right-10 lg:right-20 xl:right-0" animationDelay="1.5s" rotation="5deg" />
        <FloatingImage src={images.heroCoverLetter!} alt="Cover Letter Writing" position="bottom-32 right-20 lg:right-32 xl:right-10" animationDelay="3.5s" rotation="-4deg" />
      </React.Suspense>
    </div>
  );
}
